import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, IconButton, Tooltip, Whisper, Divider, SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/legacy/Edit';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import TablePagination from '@/components/TablePagination';
import { toastErrorMessage } from '@/utils/toasterMessage';
import ImageCell from '@/components/ImageCell';
import { RunEventDistance, RunEventDistancePaginationFilterRequest, RunEventDistanceTypeList } from '@/models/run-event-distance.model';
import { getPagedRunEventDistances } from '@/services/run-event-distance.service';
import { jsDateToZone } from '@/utils/date-time';
import { Icon } from '@rsuite/icons';
import { MdAdd, MdEmojiEvents } from 'react-icons/md';
import { AchievementTypeEnum } from '@/models/achievement.model';
import PageContent from '@/components/PageContent';
import { RunEvent } from '@/models/run-event.model';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface RunEventDistanceListProps {
  runEvent?: RunEvent;
}
const RunEventDistanceList : React.FC<RunEventDistanceListProps> = ({runEvent}) => {

  const [filterReq, setFilterReq] = useState<RunEventDistancePaginationFilterRequest>(new RunEventDistancePaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<RunEventDistance[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: RunEventDistancePaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        runEventId: runEvent?.id ?? req.runEventId,
      };      
      const result: PagedResponseModel<RunEventDistance> = await getPagedRunEventDistances(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Run Event Distances:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/run-events/distances/new`, {state: {runEventId: runEvent?.id}})}>
          Add Distance
        </Button>
        <Stack spacing={6}>                    
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={1} align="center" >
          <HeaderCell>Medal Image</HeaderCell>
          <ImageCell dataKey="medalImg" />
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Start Date</HeaderCell>
          <Cell dataKey="startDate">
            {(rowData: RunEventDistance) => jsDateToZone(rowData.startDate , rowData.city?.ianaTimeZoneId)}
          </Cell>        
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Type</HeaderCell>
          <Cell>
            {(rowData: RunEventDistance) => RunEventDistanceTypeList.findLast(t => t.value == rowData.type)?.label ?? '-'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Distance</HeaderCell>
          <Cell dataKey="distance"/>
        </Column>
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Achievement</HeaderCell>
          <Cell>
            {(rowData: RunEventDistance) => (
              rowData.hasAchievement ? 
              <Icon as={MdEmojiEvents}/> : 
              <Whisper speaker={<Tooltip>Add Achievement</Tooltip>}>
                <IconButton size='xs' color="cyan" circle appearance="ghost" 
                  icon={<Icon as={MdAdd} />}
                  onClick={() => nav(`/achievements/new`, {state: {initValue: {
                    type: AchievementTypeEnum.Event, 
                    runEventId: rowData.runEventId,
                    runEventDistanceId: rowData.id,
                    supportingCityId: rowData.city?.id,
                    supportingCountryId: rowData.city?.countryId,
                    name: `Complete: '${runEvent?.name}'`,                    
                  }}})}
                />
              </Whisper>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} fixed="right" >
        <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (         
              <IconButton onClick={() => nav(`/run-events/distances/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />  
            )}
          </Cell>
        </Column>
        
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default RunEventDistanceList;
